import {
  EntityId,
  EntitySelectEntity,
  EntitySelectSubEntity,
} from '@factorialco/f0-react/dist/experimental'
import { AvatarNameForSelectFragment } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import { Employee } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { DisplayNameStrategy, resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

export function groupByManagers(
  employees?: AvatarNameForSelectFragment[],
  excludedIds: EntityId[] = [],
  expandedElements: EntityId[] = [],
  self?: Employee,
  displayNameStrategy: DisplayNameStrategy = 'preferredOrFull'
): EntitySelectEntity[] {
  if (!employees) return []

  // When we're looking for reportees, us as an entity is not being returned, so we need to add it manually
  const isSelf = (id: number) => {
    return !excludedIds.includes(Number(self?.id)) && Math.abs(id) === self?.id
  }

  const findManager = (id: number) => {
    const manager = employees.find((value) => value.id === id)

    return manager
  }

  const managerMap = new Map<number, EntitySelectEntity>()

  employees.forEach((employee) => {
    const { id, fullName, avatar, firstName, lastName, preferredName, managerId } = employee

    const managerIdNumber = managerId ? -managerId : 0

    let managerEntity = managerMap.get(managerIdNumber)

    if (!managerEntity) {
      const name = isSelf(managerIdNumber)
        ? resolveEmployeeDisplayName({ fullName, lastName, preferredName }, displayNameStrategy)
        : findManager(-managerIdNumber)?.fullName ??
          i18n.t('employee_selector.groups.managers.no_manager')

      managerMap.set(managerIdNumber, {
        id: managerIdNumber,
        name: name ?? '',
        expanded: expandedElements.includes(managerIdNumber),
        subItems: [],
      })

      managerEntity = managerMap.get(managerIdNumber)
    }

    const subItem: EntitySelectSubEntity = {
      subId: id,
      subName: fullName,
      subAvatar: avatar ? buildUrl(avatar.url) : undefined,
      subSearchKeys: [
        preferredName ? `${preferredName} ${lastName}` : null,
        `${firstName} ${lastName}`,
      ].filter(Boolean),
    }

    if (!excludedIds.includes(id)) {
      managerEntity?.subItems?.push(subItem)
    }
  })

  const sortedManagers = orderBy(
    Array.from(managerMap.values()),
    [
      (manager) => manager.id !== 0, // No manager is first
      'name',
    ],
    ['asc', 'asc']
  )

  return Array.from(sortedManagers)
}
